import { AfterViewInit, Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from '@angular/core';
import { debounceTime, tap } from 'rxjs/operators';
import fastdom from 'fastdom';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { BaseDirective } from '../../../models/base/base-directive';
import { NumberUtils } from '../../../utils/number.utils';

/**
 * Use this directive when you need to scale down text within print cards. It plugs into the
 * isMenuReady signal, so pdfs/images will wait for the text to be scaled down before they are captured and saved.
 *
 * The appScaleDownText directive is not properly plugged into the isMenuReady signal, so do not use it when
 * building cards, or else you might find yourself with text that isn't scaled down in the final pdf/image.
 */
@Directive({
  selector: '[appPrintCardScaleDownText]',
  standalone: false
})
export class PrintCardScaleDownTextDirective extends BaseDirective implements AfterViewInit, OnChanges {
  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {
    super();
  }

  /**
   * Pixels are normally measured with whole numbers, but modern browsers can technically handle floating point pixels.
   * This is useful for scaling text to fit within a container, because it allows for more precise scaling.
   * Use if you need micro-adjustments to font size.
   *
   * If this is true, then you can change decrementFontSizeByNPixelsPerStep to a decimal number.
   */
  @Input() useFloatingPointPixels: boolean = false;
  @Input() transparentWhileScaling: boolean = false;
  @Input() lineHeightScaleVsFontSize = 1.2;
  @Input() adjustLineHeight = true;
  @Input() startScalingAfterNMillisecondsAfterViewInit = 0;
  @Input() transparentWhileScalingDebounceTimeInMilliseconds = 400;
  @Input() decrementFontSizeByNPixelsPerStep = 1;
  @Input() maxWidthInPx: number;
  @Input() maxHeightInPx: number;
  protected transparentSub: Subscription;
  protected readonly _makeTransparent = new BehaviorSubject<boolean>(false);
  protected readonly makeTransparent$ = this._makeTransparent as Observable<boolean>;

  override setupViews() {}
  override setupBindings() {}

  ngAfterViewInit() {
    // super.ngAfterViewInit(); - don't call, because we don't want rendered.next(true) to fire
    this.setupBindings();
    this.listenForTransparencyChanges();
    this.scaleToFit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.transparentWhileScaling) this.listenForTransparencyChanges();
  }

  private async scaleToFit() {
    const element = this.el.nativeElement;
    this.renderer.removeStyle(element, 'font-size');
    this.renderer.removeStyle(element, 'line-height');
    if (this.transparentWhileScaling) this.temporarilyMakeTextTransparent();
    await new Promise(r => setTimeout(r, this.startScalingAfterNMillisecondsAfterViewInit));
    await this.scaleHelper();
    this.rendered.next(true);
  }

  async scaleHelper(): Promise<void> {
    const element = this.el?.nativeElement;
    const rect = this.useFloatingPointPixels ? element?.getBoundingClientRect() : null;
    let fontSize = this.useFloatingPointPixels
      ? Number.parseFloat(window.getComputedStyle(element)?.fontSize)
      : Number.parseInt(window.getComputedStyle(element)?.fontSize, 10);
    const scrollHeight = (this.useFloatingPointPixels ? rect?.height : element?.scrollHeight) ?? 0;
    const scrollWidth = (this.useFloatingPointPixels ? rect?.width : element?.scrollWidth) ?? 0;
    if (scrollHeight > this.maxHeightInPx || scrollWidth > this.maxWidthInPx) {
      fontSize -= this.decrementFontSizeByNPixelsPerStep;
      this.temporarilyMakeTextTransparent();
      // wrapping this with fastdom crashes the browser, because it's in a while loop
      this.renderer.setStyle(element, 'font-size', `${fontSize}px`);
      const lineHeight = NumberUtils.roundToTwoDecimalPlaces(fontSize * this.lineHeightScaleVsFontSize);
      if (this.adjustLineHeight) this.renderer.setStyle(this.el.nativeElement, 'line-height', `${lineHeight}px`);
      // The older version of chrome used to render pdfs and images gets stack size exceeded errors without this delay
      await this.delay(1);
      await this.scaleHelper();
    }
  }

  async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  protected listenForTransparencyChanges(): void {
    this.transparentSub?.unsubscribe();
    if (this.transparentWhileScaling) {
      this.transparentSub = this.makeTransparent$
        .pipe(
          tap(_ => fastdom.mutate(() => this.renderer.setStyle(this.el.nativeElement, 'opacity', 0))),
          debounceTime(this.transparentWhileScalingDebounceTimeInMilliseconds),
          tap(_ => fastdom.mutate(() => this.renderer.removeStyle(this.el.nativeElement, 'opacity')))
        )
        .subscribeWhileAlive({ owner: this });
    }
  }

  protected temporarilyMakeTextTransparent(): void {
    this._makeTransparent.next(true);
  }
}
